import { useEffect, useState } from "react";

import { readPlan } from "../api/subscription";
import type { ClinicPlan } from "../api/subscription";
import { formatMoney } from "../patients/money";
import styles from "./settings.module.css";

/**
 * **What the clinic pays, read-only** (STORY-190d, BL-D7): its plan, each line's
 * monthly price and the total, in the one platform currency. The admin cannot
 * change any of it — the operator sets prices. **A line with no price shows no
 * amount rather than zero**, and while any line is unpriced there is no total.
 */
export function PlanCard(): React.JSX.Element | null {
  const [plan, setPlan] = useState<ClinicPlan | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    readPlan(controller.signal)
      .then(setPlan)
      .catch(() => {
        if (!controller.signal.aborted) setPlan(null);
      });
    return () => controller.abort();
  }, []);

  if (plan === null) return null;

  return (
    <section className={styles.card} data-testid="plan-card">
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>Your plan</h2>
      </div>
      <div className={styles.cardBody}>
        <p className={styles.lead}>
          {plan.branchCount} active {plan.branchCount === 1 ? "location" : "locations"}. Monthly, in{" "}
          {plan.currency}. To change any of this, contact DentaCore.
        </p>
        <ul>
          {plan.bill.lines.map((line) => (
            <li key={line.key} data-testid={`plan-line-${line.key}`}>
              <strong>{line.label}</strong> ·{" "}
              {line.cents === null ? "price not set yet" : formatMoney(line.cents, plan.currency)}
            </li>
          ))}
        </ul>
        <p data-testid="plan-total">
          <strong>
            {plan.bill.totalCents === null
              ? "Your monthly total has not been set yet."
              : `Total ${formatMoney(plan.bill.totalCents, plan.currency)} a month`}
          </strong>
        </p>
      </div>
    </section>
  );
}
